import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { toast } from "sonner";
import { Copy, Trash2, UserPlus } from "lucide-react";
import { Avatar, Badge, Button, ConfirmDialog, Input } from "@/components/ui";
import { UpgradePrompt } from "@/components/billing";
import { useEntitlements } from "@/packages/features-billing";
import { SettingsSection } from "./SettingsSection";

// ── Types ──────────────────────────────────────────────────────────

type MemberRole = "owner" | "admin" | "member";
type InviteRole = "admin" | "member";

interface MemberManagementProps {
  workspaceId: Id<"workspaces">;
  currentUserRole: string;
}

// ── InviteForm ─────────────────────────────────────────────────────

function InviteForm({
  workspaceId,
  memberCount,
}: {
  workspaceId: Id<"workspaces">;
  memberCount: number;
}) {
  const { canInviteMembers } = useEntitlements();
  const createInvite = useMutation(api.workspace_invites.create);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InviteRole>("member");
  const [sending, setSending] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);

  async function handleSubmit() {
    if (!email.trim()) return;
    setSending(true);
    try {
      const result = await createInvite({
        workspaceId,
        email: email.trim(),
        role,
      });
      const link = `${window.location.origin}/invite/${result.token}`;
      setInviteLink(link);
      const invitedEmail = email.trim();
      setEmail("");
      toast.success(`Invite sent! An email has been sent to ${invitedEmail}`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to send invite",
      );
    } finally {
      setSending(false);
    }
  }

  function handleCopyLink() {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink);
    toast.success("Invite link copied to clipboard");
  }

  const selectClassName =
    "h-9 w-full rounded-button border border-border bg-card px-2.5 text-[14px] text-primary outline-none cursor-pointer hover:bg-card-hover transition-colors";

  return (
    <div className="mb-5 space-y-3">
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <span className="block text-[13px] font-medium text-primary mb-1.5">
            Email address
          </span>
          <Input
            placeholder="name@company.com"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setInviteLink(null);
            }}
          />
        </div>

        <label className="w-[140px] block">
          <span className="block text-[13px] font-medium text-primary mb-1.5">
            Role
          </span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as InviteRole)}
            className={selectClassName}
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </label>

        <UpgradePrompt
          feature="additional members"
          requiredTier="pro"
          enabled={!canInviteMembers(memberCount)}
        >
          <Button
            variant="primary"
            icon={UserPlus}
            onClick={handleSubmit}
            disabled={!email.trim()}
            loading={sending}
          >
            Send Invite
          </Button>
        </UpgradePrompt>
      </div>

      {inviteLink && (
        <div className="flex items-center gap-2 p-3 bg-page border border-border rounded-card">
          <input
            type="text"
            readOnly
            value={inviteLink}
            className="flex-1 bg-transparent text-[13px] text-primary outline-none"
          />
          <button
            type="button"
            onClick={handleCopyLink}
            className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-card-hover transition-colors cursor-pointer"
            title="Copy invite link"
          >
            <Copy size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── MembersTable ───────────────────────────────────────────────────

function MembersTable({
  members,
  canManage,
  isOwner,
  onRemove,
  onRoleChange,
}: {
  members: Array<{
    _id: Id<"workspaceMemberships">;
    userId: Id<"users">;
    userName: string;
    userEmail: string;
    avatarColor: string;
    role: MemberRole;
    joinedAt: number;
  }>;
  canManage: boolean;
  isOwner: boolean;
  onRemove: (userId: Id<"users">) => void;
  onRoleChange: (userId: Id<"users">, role: "admin" | "member") => void;
}) {
  const [confirmRemove, setConfirmRemove] = useState<{
    userId: Id<"users">;
    name: string;
  } | null>(null);
  const [removing, setRemoving] = useState(false);

  async function handleConfirmRemove() {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      onRemove(confirmRemove.userId);
      toast.success(`${confirmRemove.name} has been removed`);
      setConfirmRemove(null);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to remove member",
      );
    } finally {
      setRemoving(false);
    }
  }

  function formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  return (
    <>
      <div className="border border-border rounded-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-page">
              <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                Member
              </th>
              <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                Role
              </th>
              <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                Joined
              </th>
              {canManage && (
                <th className="text-right text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5 w-[60px]">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr
                key={member._id}
                className="border-b border-border last:border-b-0 hover:bg-card-hover/50 transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar
                      name={member.userName}
                      avatarColor={member.avatarColor}
                      size="sm"
                    />
                    <div>
                      <p className="text-[13px] font-medium text-primary">
                        {member.userName}
                      </p>
                      <p className="text-[12px] text-secondary">
                        {member.userEmail}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {member.role === "owner" || !isOwner ? (
                    <span className="text-[13px] font-medium text-primary capitalize">
                      {member.role}
                    </span>
                  ) : (
                    <select
                      value={member.role}
                      onChange={(e) =>
                        onRoleChange(
                          member.userId,
                          e.target.value as "admin" | "member",
                        )
                      }
                      className="text-[13px] text-primary bg-transparent border border-border rounded-button px-2 py-1 outline-none cursor-pointer hover:bg-card-hover transition-colors"
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                    </select>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-[13px] text-secondary">
                    {formatDate(member.joinedAt)}
                  </span>
                </td>
                {canManage && (
                  <td className="px-4 py-3 text-right">
                    {member.role !== "owner" && (
                      <button
                        type="button"
                        onClick={() =>
                          setConfirmRemove({
                            userId: member.userId,
                            name: member.userName,
                          })
                        }
                        className="p-1.5 rounded-md text-muted hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                        title="Remove member"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={confirmRemove !== null}
        onClose={() => setConfirmRemove(null)}
      >
        <ConfirmDialog.Title>Remove Member</ConfirmDialog.Title>
        <ConfirmDialog.Body>
          Are you sure you want to remove {confirmRemove?.name} from this
          workspace? They will lose access immediately.
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleConfirmRemove}
          onCancel={() => setConfirmRemove(null)}
          confirmLabel="Remove"
          variant="danger"
          loading={removing}
        />
      </ConfirmDialog>
    </>
  );
}

// ── PendingInvitesTable ────────────────────────────────────────────

function PendingInvitesTable({
  invites,
  canManage,
  onRevoke,
}: {
  invites: Array<{
    _id: Id<"workspaceInvites">;
    email: string;
    role: string;
    status: string;
    token: string;
    invitedByName: string;
    createdAt: number;
    expiresAt: number;
  }>;
  canManage: boolean;
  onRevoke: (inviteId: Id<"workspaceInvites">) => void;
}) {
  const [confirmRevoke, setConfirmRevoke] = useState<{
    inviteId: Id<"workspaceInvites">;
    email: string;
  } | null>(null);
  const [revoking, setRevoking] = useState(false);

  const pendingInvites = invites.filter((inv) => inv.status === "pending");

  if (pendingInvites.length === 0) return null;

  function daysRemaining(expiresAt: number): string {
    const days = Math.ceil((expiresAt - Date.now()) / 86_400_000);
    if (days <= 0) return "Expired";
    if (days === 1) return "1 day left";
    return `${days} days left`;
  }

  function handleCopyInviteLink(token: string) {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied to clipboard");
  }

  async function handleConfirmRevoke() {
    if (!confirmRevoke) return;
    setRevoking(true);
    try {
      onRevoke(confirmRevoke.inviteId);
      toast.success(`Invite to ${confirmRevoke.email} revoked`);
      setConfirmRevoke(null);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to revoke invite",
      );
    } finally {
      setRevoking(false);
    }
  }

  return (
    <>
      <div className="mt-6">
        <h3 className="text-[14px] font-medium text-primary mb-3">
          Pending Invites ({pendingInvites.length})
        </h3>
        <div className="border border-border rounded-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-page">
                <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                  Email
                </th>
                <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                  Role
                </th>
                <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                  Expires
                </th>
                {canManage && (
                  <th className="text-right text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5 w-[120px]">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {pendingInvites.map((invite) => (
                <tr
                  key={invite._id}
                  className="border-b border-border last:border-b-0 hover:bg-card-hover/50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-[13px] font-medium text-primary">
                        {invite.email}
                      </p>
                      <p className="text-[11px] text-muted">
                        Invited by {invite.invitedByName}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      color={{
                        bg: "rgba(99,102,241,0.12)",
                        text: "#6366f1",
                      }}
                    >
                      {invite.role.charAt(0).toUpperCase() +
                        invite.role.slice(1)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[13px] text-secondary">
                      {daysRemaining(invite.expiresAt)}
                    </span>
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => handleCopyInviteLink(invite.token)}
                          className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-card-hover transition-colors cursor-pointer"
                          title="Copy invite link"
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setConfirmRevoke({
                              inviteId: invite._id,
                              email: invite.email,
                            })
                          }
                          className="p-1.5 rounded-md text-muted hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                          title="Revoke invite"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRevoke !== null}
        onClose={() => setConfirmRevoke(null)}
      >
        <ConfirmDialog.Title>Revoke Invite</ConfirmDialog.Title>
        <ConfirmDialog.Body>
          Are you sure you want to revoke the invite to {confirmRevoke?.email}?
          The invite link will no longer work.
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleConfirmRevoke}
          onCancel={() => setConfirmRevoke(null)}
          confirmLabel="Revoke"
          variant="danger"
          loading={revoking}
        />
      </ConfirmDialog>
    </>
  );
}

// ── Main Component ─────────────────────────────────────────────────

export function MemberManagement({
  workspaceId,
  currentUserRole,
}: MemberManagementProps) {
  const members = useQuery(api.workspaces.getMembers, { workspaceId });
  const canManage = currentUserRole === "owner" || currentUserRole === "admin";

  console.log({ canManage, workspaceId });

  // Only fetch invites if user has permission (owner/admin)
  const invites = useQuery(
    api.workspace_invites.listByWorkspace,
    canManage ? { workspaceId } : "skip",
  );

  const removeMember = useMutation(api.workspaces.removeMember);
  const updateRole = useMutation(api.workspaces.updateMemberRole);
  const revokeInvite = useMutation(api.workspace_invites.revoke);

  const isOwner = currentUserRole === "owner";

  return (
    <SettingsSection
      title={`Members (${members?.length ?? 0})`}
      description="Manage workspace members and invitations"
    >
      {canManage && (
        <InviteForm
          workspaceId={workspaceId}
          memberCount={members?.length ?? 0}
        />
      )}

      {members && (
        <MembersTable
          members={members}
          canManage={canManage}
          isOwner={isOwner}
          onRemove={(userId) => removeMember({ workspaceId, userId })}
          onRoleChange={(userId, role) =>
            updateRole({ workspaceId, userId, role })
          }
        />
      )}

      {invites && invites.length > 0 && (
        <PendingInvitesTable
          invites={invites}
          canManage={canManage}
          onRevoke={(inviteId) => revokeInvite({ inviteId, workspaceId })}
        />
      )}
    </SettingsSection>
  );
}
